// ── UpiQrPicker ─────────────────────────────────────────────────────────────────
// ImagePicker for a UPI QR, plus the one thing that makes the uploaded code useful to a payer:
// reading the UPI id back out of it.
//
// The problem it solves: an admin uploads their QR and leaves the UPI id blank, because the picture
// obviously "has" their id in it. To a browser it doesn't — with no VPA there is no upi:// link to
// build, so the payer's only option was to save the image and pay offline. Decoding at upload time
// fills the id in from the code itself, once, on the admin's device, and every payer after that gets
// tap-to-pay.
//
// It never blocks the upload. A code that won't decode is still a perfectly good code to scan; the
// admin is just told that tap-to-pay needs the id typed in.
import React, { useState } from 'react';
import ImagePicker from './ImagePicker';
import { decodeQrFromImageSrc } from '../utils/qrDecode';
import { parseUpiString, isLikelyVpa } from '../utils/upi';

export default function UpiQrPicker({
  value,                 // current QR data URL ('' = none)
  onChange,              // (nextValue: string) => void
  vpa,                   // the UPI id field's current value
  onVpaFound,            // (vpa: string) => void — called only when we recover one and vpa is empty
  label = 'Your UPI QR code (recommended)',
  hint = 'Open your UPI app → Receive money / My QR → screenshot it, crop to the code, and upload.',
  disabled = false,
}) {
  const [status, setStatus] = useState(null); // { kind: 'found'|'unreadable'|'busy', vpa? }

  const handleChange = async (next) => {
    onChange(next);
    if (!next) { setStatus(null); return; }

    setStatus({ kind: 'busy' });
    const text = await decodeQrFromImageSrc(next);
    const parsed = text ? parseUpiString(text) : null;

    // Only a real payee address is worth acting on — a QR can just as easily hold a URL or a wifi
    // config, and neither belongs in a payment field.
    if (parsed?.vpa && isLikelyVpa(parsed.vpa)) {
      setStatus({ kind: 'found', vpa: parsed.vpa });
      // Never overwrite something the admin typed — they may be collecting to a different account
      // from the one the QR belongs to.
      if (!String(vpa || '').trim()) onVpaFound?.(parsed.vpa);
    } else {
      setStatus({ kind: 'unreadable' });
    }
  };

  return (
    <div>
      <ImagePicker
        value={value}
        onChange={handleChange}
        preset="qr"
        label={label}
        hint={hint}
        allowUrl={false}
        previewWidth={96}
        previewHeight={96}
        disabled={disabled}
      />
      {status?.kind === 'busy' && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -4, marginBottom: 8 }}>
          Reading the code…
        </div>
      )}
      {status?.kind === 'found' && (
        <div style={{ fontSize: 11, color: 'var(--success)', marginTop: -4, marginBottom: 8, lineHeight: 1.5 }}>
          ✅ Read your UPI id from the code: <strong style={{ fontFamily: 'monospace' }}>{status.vpa}</strong>
          {' '}— payers can now tap the code to pay straight from their UPI app.
        </div>
      )}
      {status?.kind === 'unreadable' && (
        <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: -4, marginBottom: 8, lineHeight: 1.5 }}>
          ⚠️ Couldn't read a UPI id out of this image — payers will have to scan it with their camera.
          Crop tightly to the code and re-upload, or type your UPI id above to enable tap-to-pay.
        </div>
      )}
    </div>
  );
}
